import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { publishAuditAnalysisInputForDev } from "./auditNormalization";

/**
 * The High-Stakes Presentation Audit — shared state framework.
 *
 * Journey:
 *   0 Message → 1 Structure → 2 Voice → 3 Gesture → 4 Space →
 *   5 Opening → 6 Reflection → 7 Extra Context → 8 Performance Map
 *
 * Every room mutates a single global state via `setRoomAnswer` /
 * `addTags`. The Performance Map at the end will consume this state
 * (and the tags[] array) via a future Pattern Engine.
 */

export const ROOMS = [
  { id: "message",        label: "Message"         },
  { id: "structure",      label: "Structure"       },
  { id: "voice",          label: "Voice"           },
  { id: "gesture",        label: "Gesture"         },
  { id: "space",          label: "Space"           },
  { id: "opening",        label: "Opening"         },
  { id: "reflection",     label: "Reflection"      },
  { id: "performanceMap", label: "Performance Map" },
];

// Single placeholder presenter — swap for a picker later.
const DEFAULT_PRESENTER = {
  id: "placeholder-01",
  name: "Alex Rehearsal",
  role: "Senior Product Leader",
  situation: "Board update · 12 min · high-stakes",
  avatarBase: "/avatars/christos-guide",
  // Semantic pose → filename map. Any pose not listed here falls back to `neutral`.
  poses: {
    neutral:        "neutral.webp",
    walking:        "walking.webp",
    speaking:       "presenting.webp",       // alias — same asset as presenting
    presenting:     "presenting.webp",
    presentingAlt:  "presenting-alt.webp",
    presentingWide: "presenting-wide.webp",
    guidePoint:     "guide-point.webp",
    thinking:       "thinking.webp",
    listening:      "listening.webp",
    closed:         "closed.webp",
    welcomeOpen:    "welcome-open.webp",
    welcomeArms:    "welcome-arms.webp",
  },
};

// Per-room critical asset — the single background image each room paints
// on mount. Used to warm the browser cache for the NEXT room while the
// user is still reading/interacting with the current one, so the room
// transition never has to wait on a fresh network fetch.
const ROOM_BACKGROUNDS = {
  message:        "/audit/room_wide.webp",
  structure:      "/audit/bg_message.webp",
  voice:          "/audit/bg_voice.webp",
  gesture:        "/audit/bg_gesture.webp",
  space:          "/audit/bg_space.webp",
  opening:        "/audit/bg_opening.webp",
  reflection:     "/audit/bg_reflection.webp",
  performanceMap: "/audit/bg_results.webp",
};

// Per-room reset metadata — which tag namespaces and shared-reflection keys
// belong to each room, so "Reset Room" can surgically clear them.
const ROOM_RESET_META = {
  message:    { tagPrefixes: ["msg:", "message_reflection:"],      reflectionKeys: ["message"] },
  structure:  { tagPrefixes: ["struct:", "structure_reflection:"], reflectionKeys: ["structure"] },
  voice:      { tagPrefixes: ["voice:", "voice_reflection:"],      reflectionKeys: ["voice"] },
  gesture:    { tagPrefixes: ["gest:", "gesture_reflection:"],     reflectionKeys: ["gesture"] },
  space:      { tagPrefixes: ["space:", "space_reflection:"],      reflectionKeys: ["space"] },
  opening:    { tagPrefixes: ["opening:", "opening_reflection:"],  reflectionKeys: ["opening"] },
  reflection: { tagPrefixes: [], reflectionKeys: ["reflection_q1", "reflection_q2", "reflection_q3"] },
};

const ONBOARDING_KEY = "audit_onboarding_completed_v2";

const initialState = () => ({
  currentStep: 0,
  roomEpoch: 0,
  answers: {
    message:      {},
    structure:    {},
    voice:        {},
    gesture:      {},
    space:        {},
    opening:      {},
    reflection:   {},
    extraContext: {},
  },
  tags: [],
  selfReflection: "",
  extraContext: "",
  // Room 7 (Reflection) — two new REQUIRED questions, kept as dedicated
  // top-level fields (like `selfReflection`/`extraContext`) rather than
  // inside `answers`/`reflections`, so they NEVER reach the Pattern
  // Engine or `patternRead`/report content. User-provided commercial
  // context only — never analysis input, never marketing consent.
  coachingSupportInterest: null,
  nextPresentationTiming: null,
  // Room 7 — three MORE required questions, same rules as the pair
  // above (never Pattern Engine input, never marketing consent).
  // `presentationContext`/`supportPriorities` each pair with their own
  // "Other" free-text field, populated ONLY when "other" was selected.
  presentationContext: null,
  presentationContextOther: "",
  supportPriorities: [],
  supportPrioritiesOther: "",
  fundingSource: null,
  // Shared global collection — every room that captures an EXPLICIT
  // reflection answer (user-selected/user-entered, not just a thinking
  // prompt) writes into this object under its room key. Used later for
  // final feedback, webhook to n8n, Pattern Engine, email report.
  reflections: {
    message:    null,
    structure:  null,
    voice:      null,
    gesture:    null,
    space:      null,
    opening:    null,
    final:      null,
  },
  selectedPresenter: DEFAULT_PRESENTER,
  patternRead: null,
  reportSubmission: null,
  startedAt: null,
  completedAt: null,
  // Runtime UX flags — reset when the step changes.
  canProceed: true,
  overrideMessage: null,
});

const AuditContext = createContext(null);

export function AuditProvider({ children }) {
  const [state, setState] = useState(initialState);
  // Onboarding tour — played once, persisted in localStorage so it never
  // repeats after single-room resets (or full restarts).
  const [tourActive, setTourActive] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(
    () => localStorage.getItem(ONBOARDING_KEY) === "true"
  );

  const startTour = useCallback(() => setTourActive(true), []);
  const completeTour = useCallback(() => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setOnboardingCompleted(true);
    setTourActive(false);
  }, []);
  // Optional interceptor for the Continue button — a room can register a
  // callback that runs BEFORE next() advances. If the callback returns
  // false, the advance is cancelled (the room handles it internally).
  const beforeNextRef = useRef(null);

  const setBeforeNext = useCallback((fn) => {
    beforeNextRef.current = typeof fn === "function" ? fn : null;
  }, []);

  const start = useCallback(() => {
    setState((s) => ({ ...s, startedAt: s.startedAt || new Date().toISOString() }));
  }, []);

  // DEV-ONLY: publish the canonical auditAnalysisInput to window so we can
  // view/copy it after a completed Audit (window.__auditAnalysisInput /
  // window.copyAuditAnalysisInput()). No-op in production builds.
  useEffect(() => {
    publishAuditAnalysisInputForDev(state);
  }, [state]);

  // Mobile-network friendliness: warm the browser's image cache for the
  // NEXT room's background + the presenter's default pose while the user
  // is still on the current room, so advancing never stalls on a fresh
  // fetch. Pure prefetch side-effect — never touches render.
  useEffect(() => {
    const nextRoom = ROOMS[state.currentStep + 1];
    if (nextRoom) {
      const bg = ROOM_BACKGROUNDS[nextRoom.id];
      if (bg) { const img = new Image(); img.src = bg; }
    }
    const presenter = state.selectedPresenter;
    if (presenter?.avatarBase && presenter?.poses?.neutral) {
      const img = new Image();
      img.src = `${presenter.avatarBase}/${presenter.poses.neutral}`;
    }
  }, [state.currentStep, state.selectedPresenter]);

  const setRoomAnswer = useCallback((roomId, patch) => {
    setState((s) => ({
      ...s,
      answers: {
        ...s.answers,
        [roomId]: { ...(s.answers[roomId] || {}), ...patch },
      },
    }));
  }, []);

  const addTags = useCallback((newTags) => {
    if (!newTags || !newTags.length) return;
    setState((s) => ({
      ...s,
      tags: Array.from(new Set([...(s.tags || []), ...newTags])),
    }));
  }, []);

  const setSelfReflection = useCallback((value) => {
    setState((s) => ({ ...s, selfReflection: value }));
  }, []);

  const setExtraContext = useCallback((value) => {
    setState((s) => ({ ...s, extraContext: value }));
  }, []);

  const setCoachingSupportInterest = useCallback((value) => {
    setState((s) => ({ ...s, coachingSupportInterest: value }));
  }, []);

  const setNextPresentationTiming = useCallback((value) => {
    setState((s) => ({ ...s, nextPresentationTiming: value }));
  }, []);

  const setPresentationContext = useCallback((value) => {
    setState((s) => ({ ...s, presentationContext: value }));
  }, []);

  const setPresentationContextOther = useCallback((value) => {
    setState((s) => ({ ...s, presentationContextOther: value }));
  }, []);

  // Toggles a support-priority id in/out of the (max 2) selection —
  // encapsulates the "at most 2" rule here so it's enforced regardless
  // of which room component calls it (defense in depth beyond the
  // room's own disabled-button UI).
  const toggleSupportPriority = useCallback((id) => {
    setState((s) => {
      const current = s.supportPriorities || [];
      if (current.includes(id)) {
        return { ...s, supportPriorities: current.filter((x) => x !== id) };
      }
      if (current.length >= 2) return s;
      return { ...s, supportPriorities: [...current, id] };
    });
  }, []);

  const setSupportPrioritiesOther = useCallback((value) => {
    setState((s) => ({ ...s, supportPrioritiesOther: value }));
  }, []);

  const setFundingSource = useCallback((value) => {
    setState((s) => ({ ...s, fundingSource: value }));
  }, []);

  const setSelectedPresenter = useCallback((p) => {
    setState((s) => ({ ...s, selectedPresenter: p }));
  }, []);

  // Room-scoped tag replacement — a room's tags are namespaced with `${roomId}:`
  // so re-selecting cards regenerates them without leaking stale entries.
  const setTagsForRoom = useCallback((roomId, tags) => {
    setState((s) => {
      const prefix = `${roomId}:`;
      const kept = (s.tags || []).filter((t) => !t.startsWith(prefix));
      const fresh = Array.from(new Set([...kept, ...(tags || [])]));
      return { ...s, tags: fresh };
    });
  }, []);

  const setCanProceed = useCallback((canProceed) => {
    setState((s) => ({ ...s, canProceed: !!canProceed }));
  }, []);

  const setOverrideMessage = useCallback((msg) => {
    setState((s) => ({ ...s, overrideMessage: msg || null }));
  }, []);

  // Shared reflection collector — writes to state.reflections[roomKey].
  // Pass `null` to clear a reflection for a room.
  const setReflection = useCallback((roomKey, data) => {
    setState((s) => ({
      ...s,
      reflections: { ...s.reflections, [roomKey]: data },
    }));
  }, []);

  // Full Pattern Engine output — stored for the later personalised report.
  const setPatternRead = useCallback((pr) => {
    setState((s) => ({ ...s, patternRead: pr }));
  }, []);

  // Full report payload + contact details captured by the results form.
  const setReportSubmission = useCallback((payload) => {
    setState((s) => ({ ...s, reportSubmission: payload }));
  }, []);

  const goToStep = useCallback((idx) => {
    setState((s) => ({
      ...s,
      currentStep: Math.max(0, Math.min(ROOMS.length - 1, idx)),
      // Reset UX flags when navigating — each room owns its own gate/override
      canProceed: true,
      overrideMessage: null,
    }));
  }, []);

  const next = useCallback(() => {
    // Let the current room intercept the Continue click (returns false to
    // cancel — the room handles the click internally, e.g. to trigger a
    // completion dialogue before actually advancing).
    const hook = beforeNextRef.current;
    if (hook) {
      const proceed = hook();
      if (proceed === false) return;
    }
    setState((s) => {
      const nextIdx = Math.min(ROOMS.length - 1, s.currentStep + 1);
      const done = nextIdx === ROOMS.length - 1 && s.currentStep !== nextIdx;
      return {
        ...s,
        currentStep: nextIdx,
        // Gated false, not true: every room owns its own gate via its own
        // mount effect (walk-in delay, intro beats, etc). Flipping this to
        // `true` here left a brief window — before that effect's first
        // render — where a second, fast Continue click (rapid double-tap,
        // or an automated test) could skip the ENTIRE next room before it
        // ever set its own gate.
        canProceed: false,
        overrideMessage: null,
        completedAt: done ? new Date().toISOString() : s.completedAt,
      };
    });
  }, []);

  const back = useCallback(() => {
    setState((s) => ({
      ...s,
      currentStep: Math.max(0, s.currentStep - 1),
      canProceed: false,
      overrideMessage: null,
    }));
  }, []);

  // Full restart also replays the onboarding tour (single-room resets don't).
  const reset = useCallback(() => {
    localStorage.removeItem(ONBOARDING_KEY);
    setOnboardingCompleted(false);
    setTourActive(false);
    setState((s) => ({ ...initialState(), roomEpoch: (s.roomEpoch || 0) + 1 }));
  }, []);

  // Reset ONLY the current room — clears its answers, its namespaced tags and
  // its shared-reflection entries, then bumps roomEpoch so the room component
  // remounts with fresh local state.
  const resetRoom = useCallback(() => {
    setState((s) => {
      const roomId = ROOMS[s.currentStep].id;
      const meta = ROOM_RESET_META[roomId] || { tagPrefixes: [], reflectionKeys: [] };
      const tags = (s.tags || []).filter(
        (t) => !meta.tagPrefixes.some((p) => t.startsWith(p))
      );
      const reflections = { ...s.reflections };
      meta.reflectionKeys.forEach((k) => { reflections[k] = null; });
      return {
        ...s,
        answers: { ...s.answers, [roomId]: {} },
        tags,
        reflections,
        // Room 7's five extra questions live outside `answers`/
        // `reflections` (see initialState) — clear them explicitly on a
        // Room 7 reset so "Reset Room" still clears every one of Room
        // 7's answers.
        ...(roomId === "reflection" ? {
          coachingSupportInterest: null, nextPresentationTiming: null,
          presentationContext: null, presentationContextOther: "",
          supportPriorities: [], supportPrioritiesOther: "", fundingSource: null,
        } : {}),
        canProceed: false,
        overrideMessage: null,
        roomEpoch: (s.roomEpoch || 0) + 1,
      };
    });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      rooms: ROOMS,
      currentRoom: ROOMS[state.currentStep],
      start,
      next,
      back,
      goToStep,
      setRoomAnswer,
      addTags,
      setTagsForRoom,
      setCanProceed,
      setOverrideMessage,
      setSelfReflection,
      setExtraContext,
      setCoachingSupportInterest,
      setNextPresentationTiming,
      setPresentationContext,
      setPresentationContextOther,
      toggleSupportPriority,
      setSupportPrioritiesOther,
      setFundingSource,
      setSelectedPresenter,
      setBeforeNext,
      setReflection,
      setPatternRead,
      setReportSubmission,
      reset,
      resetRoom,
      tourActive,
      onboardingCompleted,
      startTour,
      completeTour,
    }),
    [state, start, next, back, goToStep, setRoomAnswer, addTags, setTagsForRoom, setCanProceed, setOverrideMessage, setSelfReflection, setExtraContext, setCoachingSupportInterest, setNextPresentationTiming, setPresentationContext, setPresentationContextOther, toggleSupportPriority, setSupportPrioritiesOther, setFundingSource, setSelectedPresenter, setBeforeNext, setReflection, setPatternRead, setReportSubmission, reset, resetRoom, tourActive, onboardingCompleted, startTour, completeTour]
  );

  return <AuditContext.Provider value={value}>{children}</AuditContext.Provider>;
}

export function useAudit() {
  const ctx = useContext(AuditContext);
  if (!ctx) throw new Error("useAudit must be used inside <AuditProvider>");
  return ctx;
}
